// LinkPickerDialog — generic tree-browsing single-select picker for linking cross-references.
//
// Displays a category/register tree with domain objects as selectable leaf items.
// Search matches both tree node names and object names. A matching tree node
// reveals all its children (objects underneath it).
//
// Usage:
//   <LinkPickerDialog
//     open={open}
//     onClose={() => setOpen(false)}
//     onLinked={() => { refetch(); setOpen(false) }}
//     title="Link Control"
//     treeNodes={controlCategories}     // from GET /categories/control/tree
//     objects={controls}                // from GET /controls
//     isLoading={treeLoading || controlsLoading}
//     existingIds={new Set(linked.map(c => c.id))}
//     getObjectId={(c) => c.id}
//     getObjectParentId={(c) => c.category_id}
//     getObjectSearchText={(c) => `${c.name} ${c.category_name}`}
//     renderObject={(c) => <ControlPickerRow control={c} />}
//     linkFn={(id) => riskApi.linkControl(riskId, id)}
//   />

import { useState, useMemo, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { X, ChevronRight, ChevronDown, Search, Loader2 } from 'lucide-react'

// --- Types ---

/** Minimal tree node shape — works for both Category and Register. */
export interface PickerTreeNode {
  id: string
  parent_id: string | null
  name: string
}

interface LinkPickerDialogProps<TNode extends PickerTreeNode, TObj> {
  open: boolean
  onClose: () => void
  onLinked: () => void
  title: string
  description?: string

  /** Flat list of tree nodes (categories or registers). */
  treeNodes: TNode[] | undefined
  /** Flat list of linkable objects (controls, risks, actions, events). */
  objects: TObj[] | undefined
  isLoading: boolean

  /** IDs of objects already linked (excluded from the list). */
  existingIds: Set<string>

  /** Extract the object's unique ID. */
  getObjectId: (obj: TObj) => string
  /** Extract the parent tree node ID the object belongs to. */
  getObjectParentId: (obj: TObj) => string
  /** Return searchable text for the object (name + any other fields). */
  getObjectSearchText: (obj: TObj) => string
  /** Render a single object row in the picker list. */
  renderObject: (obj: TObj, isSelected: boolean) => ReactNode

  /** API call to create the link. Receives the selected object ID. */
  linkFn: (objectId: string) => Promise<unknown>

  linkButtonLabel?: string
  searchPlaceholder?: string
}

// --- Internal tree node with children and objects ---

interface TreeNodeWithChildren<TNode extends PickerTreeNode, TObj> {
  node: TNode
  children: TreeNodeWithChildren<TNode, TObj>[]
  objects: TObj[]
}

// --- Component ---

export function LinkPickerDialog<TNode extends PickerTreeNode, TObj>({
  open,
  onClose,
  onLinked,
  title,
  description,
  treeNodes,
  objects,
  isLoading,
  existingIds,
  getObjectId,
  getObjectParentId,
  getObjectSearchText,
  renderObject,
  linkFn,
  linkButtonLabel = 'Link',
  searchPlaceholder = 'Search...',
}: LinkPickerDialogProps<TNode, TObj>) {
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  // Filter out already-linked objects
  const availableObjects = useMemo(() => {
    if (!objects) return []
    return objects.filter((obj) => !existingIds.has(getObjectId(obj)))
  }, [objects, existingIds, getObjectId])

  // Build tree with objects placed under their parent nodes
  const tree = useMemo(() => {
    if (!treeNodes) return []
    return buildTreeWithObjects(treeNodes, availableObjects, getObjectParentId)
  }, [treeNodes, availableObjects, getObjectParentId])

  // Compute which nodes/objects match the search and which tree nodes to show
  const { visibleNodeIds, visibleObjectIds } = useMemo(() => {
    if (!search.trim()) {
      return { visibleNodeIds: null, visibleObjectIds: null } // show everything
    }
    return computeSearchVisibility(
      tree,
      availableObjects,
      treeNodes ?? [],
      search.trim().toLowerCase(),
      getObjectId,
      getObjectSearchText,
    )
  }, [search, tree, availableObjects, treeNodes, getObjectId, getObjectSearchText])

  // Auto-expand nodes that have search matches
  const effectiveExpandedIds = useMemo(() => {
    if (!search.trim()) return expandedIds
    // When searching, expand all nodes that contain visible content
    if (!visibleNodeIds) return expandedIds
    return visibleNodeIds
  }, [search, expandedIds, visibleNodeIds])

  const linkMutation = useMutation({
    mutationFn: (objectId: string) => linkFn(objectId),
    onSuccess: () => {
      setSelectedId(null)
      setSearch('')
      setExpandedIds(new Set())
      onLinked()
    },
  })

  function handleToggle(nodeId: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(nodeId)) next.delete(nodeId)
      else next.add(nodeId)
      return next
    })
  }

  function handleSelect(objectId: string) {
    setSelectedId(objectId === selectedId ? null : objectId)
  }

  function handleLink() {
    if (selectedId) linkMutation.mutate(selectedId)
  }

  function handleClose() {
    if (!linkMutation.isPending) {
      setSelectedId(null)
      setSearch('')
      setExpandedIds(new Set())
      linkMutation.reset()
      onClose()
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

      {/* Dialog */}
      <div className="relative flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
          </div>
          <button
            onClick={handleClose}
            disabled={linkMutation.isPending}
            className="rounded p-1 hover:bg-accent"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="border-b px-6 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-9"
              autoFocus
            />
          </div>
        </div>

        {/* Tree content */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : tree.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
              No items available to link.
            </div>
          ) : (
            tree.map((branch) => (
              <TreeBranch
                key={branch.node.id}
                branch={branch}
                depth={0}
                expandedIds={effectiveExpandedIds}
                selectedId={selectedId}
                visibleNodeIds={visibleNodeIds}
                visibleObjectIds={visibleObjectIds}
                onToggle={handleToggle}
                onSelect={handleSelect}
                getObjectId={getObjectId}
                renderObject={renderObject}
              />
            ))
          )}
        </div>

        {/* Error */}
        {linkMutation.isError && (
          <div className="border-t px-6 py-2">
            <p className="text-sm text-destructive">
              {linkMutation.error instanceof Error
                ? linkMutation.error.message
                : 'Failed to create link'}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between border-t px-6 py-4">
          <div className="text-sm text-muted-foreground">
            {availableObjects.length} available
            {search.trim() && visibleObjectIds
              ? ` · ${visibleObjectIds.size} matching`
              : ''}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={linkMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLink}
              disabled={!selectedId || linkMutation.isPending}
            >
              {linkMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Linking...
                </>
              ) : (
                linkButtonLabel
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// --- Tree branch rendering ---

interface TreeBranchProps<TNode extends PickerTreeNode, TObj> {
  branch: TreeNodeWithChildren<TNode, TObj>
  depth: number
  expandedIds: Set<string>
  selectedId: string | null
  visibleNodeIds: Set<string> | null
  visibleObjectIds: Set<string> | null
  onToggle: (nodeId: string) => void
  onSelect: (objectId: string) => void
  getObjectId: (obj: TObj) => string
  renderObject: (obj: TObj, isSelected: boolean) => ReactNode
}

function TreeBranch<TNode extends PickerTreeNode, TObj>({
  branch,
  depth,
  expandedIds,
  selectedId,
  visibleNodeIds,
  visibleObjectIds,
  onToggle,
  onSelect,
  getObjectId,
  renderObject,
}: TreeBranchProps<TNode, TObj>) {
  // When searching, hide nodes that have no visible content
  if (visibleNodeIds && !visibleNodeIds.has(branch.node.id)) {
    return null
  }

  const isExpanded = expandedIds.has(branch.node.id)
  const hasContent = branch.children.length > 0 || branch.objects.length > 0

  // Filter objects when searching
  const visibleObjects = visibleObjectIds
    ? branch.objects.filter((obj) => visibleObjectIds.has(getObjectId(obj)))
    : branch.objects

  return (
    <div>
      {/* Tree node (category/register) — not selectable */}
      <div
        className={cn(
          'flex cursor-pointer items-center gap-1 rounded-sm px-1 py-1 text-sm',
          'hover:bg-accent/50',
        )}
        style={{ paddingLeft: 4 + depth * 16 }}
        onClick={() => onToggle(branch.node.id)}
      >
        {hasContent ? (
          <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </span>
        ) : (
          <span className="w-4 flex-shrink-0" />
        )}
        <span className="truncate text-muted-foreground">{branch.node.name}</span>
        {visibleObjects.length > 0 && (
          <span className="ml-auto flex-shrink-0 text-xs text-muted-foreground/60">
            {visibleObjects.length}
          </span>
        )}
      </div>

      {/* Children and objects */}
      {isExpanded && (
        <>
          {/* Child tree nodes */}
          {branch.children.map((child) => (
            <TreeBranch
              key={child.node.id}
              branch={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              selectedId={selectedId}
              visibleNodeIds={visibleNodeIds}
              visibleObjectIds={visibleObjectIds}
              onToggle={onToggle}
              onSelect={onSelect}
              getObjectId={getObjectId}
              renderObject={renderObject}
            />
          ))}

          {/* Objects under this node */}
          {visibleObjects.map((obj) => {
            const id = getObjectId(obj)
            const isSelected = id === selectedId
            return (
              <div
                key={id}
                className={cn(
                  'cursor-pointer rounded-sm px-1 py-1 transition-colors',
                  'hover:bg-accent/50',
                  isSelected && 'bg-primary/10 ring-1 ring-primary/30',
                )}
                style={{ paddingLeft: 4 + (depth + 1) * 16 }}
                onClick={() => onSelect(id)}
              >
                {renderObject(obj, isSelected)}
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}

// --- Tree building ---

function buildTreeWithObjects<TNode extends PickerTreeNode, TObj>(
  nodes: TNode[],
  objects: TObj[],
  getObjectParentId: (obj: TObj) => string,
): TreeNodeWithChildren<TNode, TObj>[] {
  // Build node map
  const nodeMap = new Map<string, TreeNodeWithChildren<TNode, TObj>>()
  for (const n of nodes) {
    nodeMap.set(n.id, { node: n, children: [], objects: [] })
  }

  // Place objects under their parent nodes
  for (const obj of objects) {
    const parentId = getObjectParentId(obj)
    const parent = nodeMap.get(parentId)
    if (parent) {
      parent.objects.push(obj)
    }
  }

  // Wire parent-child relationships
  const roots: TreeNodeWithChildren<TNode, TObj>[] = []
  for (const n of nodes) {
    const treeNode = nodeMap.get(n.id)!
    if (n.parent_id && nodeMap.has(n.parent_id)) {
      nodeMap.get(n.parent_id)!.children.push(treeNode)
    } else {
      roots.push(treeNode)
    }
  }

  return roots
}

// --- Search visibility ---

/**
 * Determines which tree nodes and objects should be visible given a search term.
 *
 * Rules:
 * - If a tree node name matches, all objects underneath it (and descendants) are visible.
 * - If an object's search text matches, it is visible and its ancestor nodes are visible.
 * - A tree node is visible if it has any visible descendants (objects or child nodes).
 */
function computeSearchVisibility<TNode extends PickerTreeNode, TObj>(
  tree: TreeNodeWithChildren<TNode, TObj>[],
  _objects: TObj[],
  allNodes: TNode[],
  searchLower: string,
  getObjectId: (obj: TObj) => string,
  getObjectSearchText: (obj: TObj) => string,
): { visibleNodeIds: Set<string>; visibleObjectIds: Set<string> } {
  const visibleNodeIds = new Set<string>()
  const visibleObjectIds = new Set<string>()

  // Build parent lookup for ancestor traversal
  const parentMap = new Map<string, string | null>()
  for (const n of allNodes) {
    parentMap.set(n.id, n.parent_id)
  }

  function markAncestorsVisible(nodeId: string) {
    let current: string | null | undefined = nodeId
    while (current) {
      if (visibleNodeIds.has(current)) break
      visibleNodeIds.add(current)
      current = parentMap.get(current)
    }
  }

  function markAllObjectsInBranch(branch: TreeNodeWithChildren<TNode, TObj>) {
    for (const obj of branch.objects) {
      visibleObjectIds.add(getObjectId(obj))
    }
    for (const child of branch.children) {
      visibleNodeIds.add(child.node.id)
      markAllObjectsInBranch(child)
    }
  }

  function traverse(branch: TreeNodeWithChildren<TNode, TObj>) {
    const nodeNameMatches = branch.node.name.toLowerCase().includes(searchLower)

    if (nodeNameMatches) {
      // Node name matches — show all objects and descendants
      markAncestorsVisible(branch.node.id)
      markAllObjectsInBranch(branch)
    }

    // Check individual objects
    for (const obj of branch.objects) {
      if (nodeNameMatches || getObjectSearchText(obj).toLowerCase().includes(searchLower)) {
        visibleObjectIds.add(getObjectId(obj))
        markAncestorsVisible(branch.node.id)
      }
    }

    // Recurse into children
    for (const child of branch.children) {
      traverse(child)
    }
  }

  for (const branch of tree) {
    traverse(branch)
  }

  return { visibleNodeIds, visibleObjectIds }
}
